import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";
import MasterListPage from "./MasterListPage";
import ActionDropdown from "Common/ActionDropdown/ActionDropdown";
// import { fetchSalesOrders, exportSalesOrdersCSV } from "slices/salesorder/thunk";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import NameWithDate from "Common/Components/NameWithDate.tsx/NameWithDate";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { formatIndianAmount } from "helpers/formatIndianAmount";
import { showConfirm } from "helpers/swalHelper";
import { useDispatch } from "react-redux";
import { deleteProductSalesOrder, handleRevertProductSalesOrderStatus } from "slices/sales_order/thunk";
import { toast } from "react-toastify";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { app_modules } from "helpers/apps_modules_list";
import usePermission from "hooks/usePermission";
import Tooltip from "@mui/material/Tooltip";
import { fetchAllProductSalesOrders } from "slices/sales_order/thunk";
import Chip from "components/Chips/Chip";

interface SalesOrderPageProps {
  type?: "product"; // Product specific, defaults to product
  /** Tags-search: switch tabs without route change */
  onTagsSearchTabNavigate?: (tabValue: string) => void;
  /** When set (tags-search), use instead of :tab from URL */
  tagsSearchActiveTab?: "sales_order_log" | "draft";
  noWrapper?: boolean;
  forceShowOnTagsSearch?: boolean;
}

const SalesOrderDraft: React.FC<SalesOrderPageProps> = ({
  type = "product",
  onTagsSearchTabNavigate,
  tagsSearchActiveTab,
  noWrapper = false,
  forceShowOnTagsSearch = false,
}) => {
  const { tab } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const [reloadFlag, setReloadFlag] = useState(0);

  // Get permissions for product sales orders
  const { canView, canUpdate, canDelete } = usePermission(
    app_modules?.sales_order?.app_name,
    app_modules?.sales_order?.modules?.product
  );

  const activeType = "product"; // Hardcoded for product
  const activeTab = tagsSearchActiveTab ?? tab;

  // dynamic title
  const title =
    activeTab === "sales-order-log"
      ? "Order Log"
      : activeTab === "junk"
      ? "Junk"
      : "Draft";

  // dynamic add label
  const addButtonLabel = "Create Sales Order";

  // dynamic add route
  const addRoute = `/sales_order/add_product_sales_order`;

  const handleView = (salesOrderNo: any) => {
    // redirect to single sales order page
    navigate(`/tags-search/sales_order/${activeType}/${activeTab}/view/${salesOrderNo || salesOrderNo.sales_order_service_id || salesOrderNo.id}`);
  };

  const handleEdit = (sales_order_product_id: string) => {
    // redirect to edit form
    navigate(`/sales_order/${activeType}/edit/${sales_order_product_id}`, {
      state: { tab: activeTab }
    });
  };

  const handleDelete = async (sales_order_product_id: string) => {
    const result = await showConfirm(
      "Are you sure you want to delete this sales order?",
      "Delete Sales Order",
      "Yes, Delete",
      "Cancel"
    );
    
    if (result.isConfirmed) {
      try {
        await dispatch(handleRevertProductSalesOrderStatus(sales_order_product_id)).unwrap();
        toast.success("Sales order deleted successfully");
        // Trigger refresh by incrementing reloadFlag
        setReloadFlag(prev => prev + 1);
      } catch (error: any) {
        toast.error(error?.message || "Failed to delete sales order");
      }
    }
  };


  const handleRestore = (sales_order_product_id: string) => {
    // dispatch(restoreSalesOrder(salesOrderNo))
  };

  const handleFollowUp = (sales_order_product_id: string) => {
    // redirect to follow up form
    navigate(`/sales_order/followups/${activeType}/${sales_order_product_id}`);
  };
  const handleCreateSalesOrder = (sales_order_product_id: string) => {
    // redirect to create sales order form
    navigate(`/sales-order/${activeType}/create-from-enquiry/${sales_order_product_id}`);
  };

  const getColumns = () => {
    const commonColumns = [
      // {
      //   header: "Sr. No.",
      //   accessorKey: "sr_no",
      //   enableColumnFilter: false,
      //   enableSorting: false,
      //   cell: ({ row, table }: { row: any; table: any }) =>
      //     table.getState().pagination.pageIndex *
      //       table.getState().pagination.pageSize +
      //     row.index +
      //     1,
      // },
      {
        header: "Sales Order No.",
        accessorKey: "sales_order_number",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT.SALES_ORDER.PRODUCT.FILTER_DROPDOWN("sales_order_number")}&stage=draft`,
        cell: ({ row }: { row: any }) =>
          row.original.sales_order_number || "-",
      },
      {
        header: "Quotation Reference",
        accessorKey: "quotation_number",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT.SALES_ORDER.PRODUCT.FILTER_DROPDOWN("quotation_number")}&stage=draft`,
        cell: ({ row }: { row: any }) =>
          row.original.quotation_number || "-",
      },

      {
        header: "Customer Name",
        accessorKey: "customer_details.company_name",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT.SALES_ORDER.PRODUCT.FILTER_DROPDOWN("company_name")}&stage=draft`,
        cell: ({ row }: { row: any }) =>
          row.original.customer_details?.company_name || "-",
      },
      {
        header: "SO Date",
        accessorKey: "sales_order_date",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "date" as const,
        filterFetchUrl: `${API_ENDPOINT.SALES_ORDER.PRODUCT.FILTER_DROPDOWN("sales_order_date")}&stage=draft`,
        cell: ({ row }: { row: any }) => (
          <FormattedDate date={row.original.sales_order_date} />
        ),
      },
      {
        header: "Agent",
        accessorKey: "agent_name",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT.SALES_ORDER.PRODUCT.FILTER_DROPDOWN("agent_name")}&stage=draft`,
        cell: ({ row }: { row: any }) =>
          row.original.agent_name || "-",
      },

      {
        header: "Grand  Total Amount",
        accessorKey: "grand_total_amount",
        enableColumnFilter: false,
        enableSorting: false,
        filterFetchUrl: `${API_ENDPOINT.SALES_ORDER.PRODUCT.FILTER_DROPDOWN("grand_total_amount")}&stage=draft`,
        cell: ({ row }: { row: any }) => {
          const val = row.original.grand_total_amount;
          const sym = row.original.currency_symbol || "₹";
          return val != null && val !== "" ? `${sym} ${formatIndianAmount(val)}` : "-";
        },
      },
      {
        header: "Status",
        accessorKey: "sales_order_status",
        id: "status",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT.SALES_ORDER.PRODUCT.FILTER_DROPDOWN("sales_order_status")}&stage=draft`,
        cell: ({ row }: { row: any }) => {
          return row.original.sales_order_status || "-";
        },
        // cell: ({ row }: { row: any }) => {
        //   return <StatusDropdown currentStatus={row.original.sales_order_status} itemId={row.original.sales_order_id} onStatusChange={handleStatusChange} statusOptions={salesOrderStatusOptions} disabled={false} />
        // }
      },
      // {
      //   header: "PGB Status",
      //   accessorKey: "pgb_status",
      //   id: "pgb_status",
      //   enableColumnFilter: true,
      //   enableSorting: false,
      //   filterType: "dropdown" as const,
      //   filterFetchUrl: `/sales_order/${activeType}/dynamic_dropdown/status?stage=draft`,
      // },
      {
        header: "Assigned To",
        accessorKey: "assigned_members",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT.SALES_ORDER.PRODUCT.FILTER_DROPDOWN("assigned_members")}&stage=draft`,

        cell: ({ row }: { row: any }) => {
          const members = row.original?.assigned_members || [];

          if (!members.length) return "-";

          const names = members.map(
            (m: any) =>
              `${m.first_name || ""} ${m.last_name || ""}`.trim() || m.username
          );

          const visible = names.slice(0, 4);
          const remaining = names.length - visible.length;

          return (
            <Tooltip
              arrow
              placement="top"
              title={names.join("\n")}
              componentsProps={{
                tooltip: {
                  sx: {
                    whiteSpace: "pre-line",
                    maxWidth: 250,
                    fontSize: "13px",
                  },
                },
              }}
            >
              <div style={{ cursor: "pointer", lineHeight: 1.4 }}>
                {visible.map((name: string, index: number) => (
                  <div key={index}>{name}</div>
                ))}

                {remaining > 0 && (
                  <div style={{ fontSize: "12px", color: "#6c757d" }}>
                    +{remaining} more
                  </div>
                )}
              </div>
            </Tooltip>
          );
        },
      },
      
      {
        header: "Notes",
        accessorKey: "notes",
        enableColumnFilter: false,
        enableSorting: false,
        minSize: 200,
        size: 200,
        cell: ({ row }: { row: any }) => (
          <div
            style={{
              whiteSpace: "normal",
              wordWrap: "break-word",
              maxWidth: "200px",
              minWidth: "200px",
            }}
          >
            {row.original.notes || "-"}
          </div>
        ),
        // filterType: "dropdown" as const,
        // filterFetchUrl: `/sales_order/${activeType}/dynamic_dropdown/notes?stage=draft`,
      },
             {
              header: "Tags",
              accessorKey: "tags",
              enableColumnFilter: false,
              enableSorting: false,
              cell: ({ row }: { row: any }) => {
                const tags: string[] = row.original.tags || [];
                if (!tags.length) return "-";
      
                const maxVisible = 3;
                const visible = tags.slice(0, maxVisible);
                const remaining = tags.length - visible.length;
                const tooltipText = tags.join(", ");
      
                return (
                  <Tooltip
                    arrow
                    placement="top"
                    title={tooltipText}
                    componentsProps={{
                      tooltip: {
                        sx: {
                          maxWidth: 300,
                          fontSize: "13px",
                          whiteSpace: "normal",
                        },
                      },
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "4px",
                        minWidth: "150px",
                        cursor: "pointer",
                      }}
                    >
                      {visible.map((tag: string) => (
                        <Chip key={tag}>{tag}</Chip>
                      ))}
                      {remaining > 0 && <Chip>+{remaining} more</Chip>}
                    </div>
                  </Tooltip>
                );
              },
            },
      {
        header: "Created By/Date",
        accessorKey: "created_at",
          enableColumnFilter: false,
        enableSorting: false,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const user = row.original.created_by_user;
          const displayName = user
            ? (typeof user === 'string'
                ? user
                : user.username || '-')
            : '-';
          return (
            <NameWithDate
              name={displayName}
              date={row.original.created_at}
            />
          );
        },
      },
      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const user = row.original.updated_by_user;
          const displayName = user
            ? (typeof user === 'string'
                ? user
                  :user.username || '-')
            : '-';
          return (
            <NameWithDate
              name={displayName}
              date={row.original.updated_at}
            />
          );
        },
      },
    ];

    const actionColumn = {
      header: "Action",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: { row: any }) => {
        const sales_order_product_id = row.original.sales_order_product_id;

        const actions = [
          ...(canView ? [{
            label: "View",
            permission: canView,
            icon: <VisibilityIcon fontSize="small" color="info" />,
            onClick: () => handleView(sales_order_product_id),
          }] : []),
          ...(canUpdate ? [{
            label: "Edit",
            permission: canUpdate,
            icon: <EditIcon fontSize="small" color="primary" />,
            onClick: () => handleEdit(sales_order_product_id),
          }] : []),
          ...(canUpdate ? [{
            label: "Delete",
            permission: canUpdate,
            icon: <DeleteOutlineIcon fontSize="small" color="error" />,
            onClick: () => handleDelete(sales_order_product_id),
            className: "text-danger",
          }] : []),
        ];

        // return <ActionDropdown actions={actions} />;
        return <DynamicMenuItem row={row.original} menuItems={actions} />;
      },
    };
    return [...commonColumns, actionColumn];
  };

  return (
    <>
      <MasterListPage
        title={`Sales ${title}`}
        addButtonLabel={addButtonLabel}
        addRoute={addRoute}
        fetchThunk={"fetchSalesOrders"}
        exportThunk={"exportSalesOrdersCSV"}
        columns={getColumns()}
        statusToggleUrl={`/sales-order/product`}
        activeTab={activeTab}
        activeType={"product"}
        handleDelete={handleDelete}
        reloadFlag={reloadFlag}
        noWrapper={noWrapper}
        onTagsSearchTabNavigate={onTagsSearchTabNavigate}
        forceShowOnTagsSearch={forceShowOnTagsSearch}
      />
    </>
  );
};

export default SalesOrderDraft;
